/**
 * Top Command Header
 * Blue command bar with search, lens/sort, and global actions
 */

import { useRef, useEffect, useCallback } from 'react'
import { makeStyles, tokens, Button, Tooltip } from '@fluentui/react-components'
import { MoreHorizontalRegular } from '@fluentui/react-icons'
import { SearchInput } from './SearchInput'
import { FilterChips } from './FilterChips'
import { LensSortMenu } from './LensSortMenu'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusLarge,
    flexShrink: 0,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginLeft: 'auto',
  },
  actionsButton: {
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      color: tokens.colorNeutralForegroundOnBrand,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
})

export function TopCommandHeader() {
  const styles = useStyles()
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Global keyboard shortcut: Ctrl+K or "/" to focus search
  const handleGlobalKeyDown = useCallback((event: KeyboardEvent) => {
    // "/" when not in an input
    const isInInput =
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      (event.target instanceof HTMLElement && event.target.isContentEditable)

    if (event.key === '/' && !isInInput) {
      event.preventDefault()
      searchInputRef.current?.focus()
      return
    }

    // Ctrl+K (or Cmd+K on Mac)
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault()
      searchInputRef.current?.focus()
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [handleGlobalKeyDown])

  return (
    <div className={styles.root}>
      <LensSortMenu />
      <SearchInput ref={searchInputRef} />
      <FilterChips />
      <div className={styles.rightSection}>
        <Tooltip content="Global Actions (Ctrl+K)" relationship="label">
          <Button
            appearance="transparent"
            icon={<MoreHorizontalRegular />}
            className={styles.actionsButton}
          >
            Actions
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}
